import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    organizationName: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/organizations/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.organizationName,
          adminEmail: formData.email,
          adminPassword: formData.password,
          adminName: formData.name,
          plan: 'BASIC',
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Auto-login with the returned token
        if (data.data?.token) {
          localStorage.setItem('token', data.data.token);
          setTimeout(() => {
            navigate('/dashboard');
            window.location.reload(); // Reload to update auth state
          }, 1500);
        }
      } else {
        setError(data.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="main-content">
      <div className="auth-layout">
        <aside className="auth-aside">
          <Link to="/" className="brand-lockup od-row">
            <span className="brand-symbol"><Shield className="od-icon" /></span>
            <span>AirGap<span className="brand-suite"> Suite</span></span>
          </Link>
          <div className="auth-aside-copy">
            <p className="auth-eyebrow">Start with a workspace</p>
            <h2>Every license.<br />A clear owner.</h2>
            <p>Create your organization and administrator account. Then define the products, policies, and access your team needs.</p>
            <ul>
              <li><CheckCircle2 className="od-icon" />Organization-level management</li>
              <li><CheckCircle2 className="od-icon" />Role-based team access</li>
              <li><CheckCircle2 className="od-icon" />Connected and offline licensing</li>
            </ul>
          </div>
          <p className="auth-aside-foot">AirGap Suite / Organization setup</p>
        </aside>

        <div className="auth-form-region">
          <div className="auth-form-wrap">
            <Link to="/" className="text-link">
              <ArrowLeft className="od-icon" />
              Back to home
            </Link>
            <p className="page-eyebrow">Organization setup</p>
            <h1>Create your workspace</h1>
            <p className="auth-description">Your account will administer this organization.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="request-notice error !m-0" role="alert">
                  <AlertCircle className="od-icon" />
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="request-notice success !m-0" role="status">
                  <CheckCircle2 className="od-icon" />
                  <span>Registration successful! Redirecting to dashboard...</span>
                </div>
              )}

              <div className="od-field [--od-gap:8px]">
                <Label htmlFor="organizationName">Organization name <span className="text-muted-foreground">*</span></Label>
                <Input
                  id="organizationName"
                  name="organizationName"
                  type="text"
                  placeholder="Acme Corporation"
                  value={formData.organizationName}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                />
              </div>

              <div className="od-field [--od-gap:8px]">
                <Label htmlFor="name">Your name <span className="text-muted-foreground">*</span></Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                />
              </div>

              <div className="od-field [--od-gap:8px]">
                <Label htmlFor="email">Work email <span className="text-muted-foreground">*</span></Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                />
              </div>

              <div className="od-field [--od-gap:8px]">
                <Label htmlFor="password">Password <span className="text-muted-foreground">*</span></Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  minLength={8}
                />
                <span className="text-xs text-muted-foreground">Must be at least 8 characters</span>
              </div>

              <div className="od-field [--od-gap:8px]">
                <Label htmlFor="confirmPassword">Confirm password <span className="text-muted-foreground">*</span></Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  minLength={8}
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading || success}>
                {loading ? 'Creating workspace...' : success ? 'Success!' : 'Create workspace'}
              </Button>
            </form>

            <p className="auth-register">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
